import { supabase } from '../lib/supabase';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

export const ensureValidSession = async (): Promise<boolean> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      // First check if we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Error getting session:', sessionError);
        isRefreshing = false;
        refreshPromise = null;
        return false;
      }

      if (!session) {
        console.log('No session found');
        isRefreshing = false;
        refreshPromise = null;
        return false;
      }

      // Check if session is expired or about to expire (within 5 minutes)
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const fiveMinutes = 5 * 60;

      if (expiresAt && expiresAt > (now + fiveMinutes)) {
        // Session is still valid and has more than 5 minutes left
        isRefreshing = false;
        refreshPromise = null;
        return true;
      }

      // Session is expired or about to expire, try to refresh
      console.log('Session expiring soon, refreshing...');
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

      if (refreshError) {
        console.error('Failed to refresh session:', refreshError);
        isRefreshing = false;
        refreshPromise = null;
        // Return true if session is still technically valid even if refresh failed
        return expiresAt ? expiresAt > now : false;
      }

      // Refresh succeeded
      isRefreshing = false;
      refreshPromise = null;
      return !!refreshData.session;
    } catch (error) {
      console.error('Unexpected session error:', error);
      isRefreshing = false;
      refreshPromise = null;
      return false;
    }
  })();

  return refreshPromise;
};

export const setupVisibilityListener = () => {
  if (typeof document === 'undefined') return;

  const handleVisibilityChange = async () => {
    if (!document.hidden) {
      const sessionValid = await ensureValidSession();

      if (!sessionValid) {
        // Session couldn't be refreshed - notify user
        const shouldReload = confirm(
          'Din session har gått ut efter att appen varit inaktiv.\n\n' +
          'Klicka OK för att ladda om sidan och logga in igen.\n\n' +
          'Tips: För att undvika detta problem i framtiden, se till att appen inte är inaktiv för länge.'
        );

        if (shouldReload) {
          window.location.reload();
        }
      }
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

export const setupOnlineListener = (callback: (isOnline: boolean) => void) => {
  if (typeof window === 'undefined') return;

  const handleOnline = () => {
    callback(true);
    ensureValidSession();
  };

  const handleOffline = () => {
    callback(false);
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  callback(navigator.onLine);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
};

interface RetryOptions {
  maxRetries?: number;
  delayMs?: number;
  backoff?: boolean;
}

export const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> => {
  const { maxRetries = 3, delayMs = 1000, backoff = true } = options;
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        await ensureValidSession();
      }

      return await operation();
    } catch (error: any) {
      lastError = error;

      if (attempt < maxRetries) {
        const delay = backoff ? delayMs * Math.pow(2, attempt) : delayMs;
        console.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};
